import {
  BooleanOperator,
  ConditionTab,
  ConditionType,
  DatetimeOperator,
  FrequencyOperator,
  ListOperator,
  NumberOperator,
  StringOperator,
  Type,
} from './enums/builder-enums';
import {IntegrationType} from './enums/connect-enums';
import {Property, WindowUnit} from './enums/expression-enums';
import {FilterOperator} from './types/builder-state-types';

export const CONDITION_TYPE_TO_TAB_MAPPING = {
  [ConditionType.Cohorts]: ConditionTab.Cohorts,
  [ConditionType.Events]: ConditionTab.Events,
  [ConditionType.People]: ConditionTab.People,
};

export const LITERAL_NAMES = [
  `A`,
  `B`,
  `C`,
  `D`,
  `E`,
  `F`,
  `G`,
  `H`,
  `I`,
  `J`,
  `K`,
  `L`,
  `M`,
  `N`,
  `O`,
  `P`,
  `Q`,
  `R`,
  `S`,
  `T`,
  `U`,
  `V`,
  `W`,
  `X`,
  `Y`,
  `Z`,
];
export const MAX_LITERALS = LITERAL_NAMES.length;
export const MAX_CONDITIONS = 15;
export const LITERAL_NAME_REGEX = new RegExp(`[${LITERAL_NAMES.join(``)}]\\d*`, `g`);

export const DEFAULT_FREQUENCY_VALUE = 1;
export const ANY_EVENT = ``;
export const FIRST_EVENT = `$first_event`;
export const DEFAULT_EVENT = ANY_EVENT;

export const DEFAULT_RELATIVE_NUM_DAYS = 7;
export const DEFAULT_WINDOW_UNIT = WindowUnit.Day;

export const DEFAULT_PEOPLE_PROPERTY_OPTIONS = {
  operator: DatetimeOperator.Relative,
  property: `$created`,
  propertyType: Property.User,
  type: Type.Datetime,
  value: {
    window: {
      unit: WindowUnit.Day,
      value: 30,
    },
  },
};
export const MAX_LABEL_ENTRIES = 3;
export const SHOW_MORE_LIMIT = 5;
export const TABS_LIMIT_NORMAL_WIDTH = 4;
export const MAX_COHORT_NAME_LENGTH = 190;
export const FUNNEL_CLAUSE_OPERATOR: any = null;

export const FREQUENCY_OPERATOR_LIST = [
  FrequencyOperator.Between,
  FrequencyOperator.AtLeast,
  FrequencyOperator.GreaterThan,
  FrequencyOperator.LessThan,
  FrequencyOperator.EqualTo,
];

export const FILTER_TYPE_LIST = [Type.String, Type.Number, Type.Boolean, Type.Datetime, Type.List];

const STRING_FILTER_OPERATOR_LIST = [
  StringOperator.Contains,
  StringOperator.DoesNotContain,
  StringOperator.Equals,
  StringOperator.DoesNotEqual,
  StringOperator.Set,
  StringOperator.NotSet,
];

const NUMBER_FILTER_OPERATOR_LIST = [
  NumberOperator.Between,
  NumberOperator.AtLeast,
  NumberOperator.GreaterThan,
  NumberOperator.LessThan,
  NumberOperator.EqualTo,
  NumberOperator.NotEqualTo,
];

const DATETIME_FILTER_OPERATOR_LIST = [
  DatetimeOperator.Between,
  DatetimeOperator.After,
  DatetimeOperator.On,
  DatetimeOperator.RelativeBeforeInPast,
  DatetimeOperator.Relative,
  DatetimeOperator.RelativeFuture,
];

export const FILTER_OPERATORS: {[filter: string]: FilterOperator[]} = {
  [Type.String]: STRING_FILTER_OPERATOR_LIST,
  [Type.Number]: NUMBER_FILTER_OPERATOR_LIST,
  [Type.Datetime]: DATETIME_FILTER_OPERATOR_LIST,
  [Type.Boolean]: [BooleanOperator.True, BooleanOperator.False],
  [Type.List]: [ListOperator.Contains, ListOperator.DoesNotContain],
};

export const DEFAULT_FILTER_OPERATOR: {[filter: string]: FilterOperator} = {
  [Type.String]: StringOperator.Equals,
  [Type.Number]: NumberOperator.EqualTo,
  [Type.Datetime]: DatetimeOperator.Relative,
  [Type.Boolean]: BooleanOperator.True,
  [Type.List]: ListOperator.Contains,
};

export const EXPORT_INTEGRATIONS_INFO = {
  [IntegrationType.Braze]: {
    exportStartedMessage: `Your cohort export to Braze has begun.`,
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360001243663#use-the-mixpanel-cohort-to-create-a-segment-in-braze`,
    helpLinkText: `Setting up the Mixpanel Cohort in Braze`,
    noUsersText: `There are no user profiles with a Braze external user id in this cohort`,
    tooltipText: `Only user profiles with a Braze external user id will be exported`,
  },
  [IntegrationType.Marketo]: {
    duplicateExternalInfoError: `This custom field has already been used`,
    exportStartedMessage: `Your cohort export to Marketo has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Custom field name`,
      param: `marketo_custom_field_name`,
      placeholder: `Marketo Custom Field to update`,
      title: `Custom field name`,
    },
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005719646-Integrating-Mixpanel-and-Marketo` +
      `#marketo-integration-setup`,
    helpLinkText: `Setting up Custom Fields in Marketo`,
    maxExportUsers: 30000000,
    maxExportUsersText: `The maximum exportable cohort size is 30 million user profiles at this time.`,
    noUsersText: `There are no user profiles with $email in this cohort`,
    tooltipText: `Only user profiles with $email will be exported`,
  },
  [IntegrationType.UrbanAirship]: {
    duplicateExternalInfoError: `This tag has already been used`,
    exportStartedMessage: `Your cohort export to Airship has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Tag name`,
      param: `tag`,
      placeholder: `Airship tag to add`,
      title: `Airship tag`,
    },
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005712363-Integrating-Mixpanel-and-Urban-Airship` +
      `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Mixpanel Tag Group in Airship`,
    maxExportUsers: 15000000,
    maxExportUsersText: `The maximum exportable cohort size is 15 million user profiles at this time.`,
    noUsersText: `There are no user profiles with an Airship Channel ID or a Named User ID in this cohort`,
    tooltipText: `Only user profiles with an Airship Channel ID or a Named User ID will be exported`,
  },
  [IntegrationType.FacebookAds]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360035583452-Facebook-Ads-Integration`,
    helpLinkText: `Setting up the Facebook Ads Integration`,
    exportStartedMessage: `Your cohort export to Facebook Ads has begun.`,
    tooltipText: `Only user profiles with an email, a phone number, a first name, and a last name will be exported`,
  },
  [IntegrationType.Iterable]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360039403631-Iterable-Integration`,
    helpLinkText: `Setting up the Iterable Integration`,
    exportStartedMessage: `Your cohort export to Iterable has begun.`,
    tooltipText: `Only user profiles with an email will be exported`,
  },
  [IntegrationType.GoogleAds]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360039256492-Google-Ads-Integration`,
    helpLinkText: `Setting up the Google Adwords Integration`,
    exportStartedMessage: `Your cohort export to Google Adwords has begun.`,
    tooltipText: `Only user profiles with an email and a phone number will be exported`,
  },
  [IntegrationType.TestPartnerWebhookIntegration]: {
    helpLink: `www.google.com`,
    helpLinkText: `No help article for this Integration yet`,
    exportStartedMessage: `Your cohort export to Test Partner Webhook Integration has begun.`,
    tooltipText: `Only user profiles with an email will be exported`,
  },
  [IntegrationType.MoengageWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360049332572-MoEngage-Integration#matching-users-between-moengage-and-mixpanel`,
    helpLinkText: `Setting up the MoEngage integration`,
    exportStartedMessage: `Your cohort export to Moengage has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.KameleoonWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360055834811-Kameleoon-Integration`,
    helpLinkText: `Setting up the Kameleoon integration`,
    exportStartedMessage: `Your cohort export to Kameleoon has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.MailchimpWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360057106912-Mailchimp-Integration`,
    helpLinkText: `Setting up the Mailchimp integration`,
    exportStartedMessage: `Your cohort export to Mailchimp has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.VwoWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360055654412-VWO-Integration`,
    helpLinkText: `Setting up the VWO integration`,
    exportStartedMessage: `Your cohort export to VWO has begun.`,
    tooltipText: `Only user profiles with a "$vwo_user_id" property will be exported`,
  },
  [IntegrationType.TaplyticsWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360056090251-Taplytics-Integration`,
    helpLinkText: `Setting up the Taplytics integration`,
    exportStartedMessage: `Your cohort export to Taplytics has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.LeanplumWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360050702631-Leanplum-Integration#matching-users-between-leanplum-and-mixpanel`,
    helpLinkText: `Setting up the Leanplum integration`,
    exportStartedMessage: `Your cohort export to Leanplum has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.InsiderWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360051368091-Insider-Integration#matching-users-between-insider-and-mixpanel`,
    helpLinkText: `Setting up the Insider integration`,
    exportStartedMessage: `Your cohort export to Insider has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.AppcuesWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360051314911-Appcues-Integration#matching-users-between-appcues-and-mixpanel`,
    helpLinkText: `Setting up the Appcues integration`,
    exportStartedMessage: `Your cohort export to Appcues has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.ChameleonWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360050608232-Chameleon-Integration#matching-users-between-chameleon-and-mixpanel`,
    helpLinkText: `Setting up the Chameleon integration`,
    exportStartedMessage: `Your cohort export to Chameleon has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.OnesignalWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360051018011-OneSignal-Integration#matching-users-between-onesignal-and-mixpanel`,
    helpLinkText: `Setting up the OneSignal integration`,
    exportStartedMessage: `Your cohort export to OneSignal has begun.`,
    tooltipText: `Only user profiles with a "$onesignal_user_id" property will be exported`,
  },
  [IntegrationType.WebengageWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360061162352-WebEngage-Integration`,
    helpLinkText: `Setting up the WebEngage integration`,
    exportStartedMessage: `Your cohort export to WebEngage has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.XtremepushWebhook]: {
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360059064332`,
    helpLinkText: `Setting up the Xtremepush integration`,
    exportStartedMessage: `Your cohort export to Xtremepush has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
};

export const EXPORT_TOGGLE_OPTIONS = [
  {
    text: {
      [IntegrationType.Braze]: `All users in the cohort will be updated one time.`,
      [IntegrationType.Marketo]: `All users in the cohort will be updated with the custom field one time.`,
      [IntegrationType.UrbanAirship]: `All users in the cohort will be updated with the tag one time.`,
      [IntegrationType.FacebookAds]: `All users in the cohort will be uploaded to the Business Manager, replacing the old one.`,
      [IntegrationType.GoogleAds]: `All users in the cohort will be uploaded as a Custom Audience, replacing the old one.`,
      [IntegrationType.Iterable]: `All users in the cohort will be uploaded as an Iterable List, replacing the old one.`,
      [IntegrationType.TestPartnerWebhookIntegration]: `All users in the cohort will exported to the Webhook destination of choice.`,
      [IntegrationType.MoengageWebhook]: `All exported users will be sent as a MoEngage custom segment.`,
      [IntegrationType.LeanplumWebhook]: `Users in the cohort that match successfully into Leanplum will be uploaded as a Leanplum Audience, replacing the old one.`,
      [IntegrationType.ChameleonWebhook]: `All exported users will be sent as a Chameleon custom segment.`,
      [IntegrationType.InsiderWebhook]: `All exported users will be sent as an Insider custom segment.`,
      [IntegrationType.AppcuesWebhook]: `All exported users will be sent as an Appcues custom segment.`,
      [IntegrationType.OnesignalWebhook]: `All exported users will be sent as a OneSignal custom segment.`,
      [IntegrationType.KameleoonWebhook]: `All exported users will be sent as a Kameleoon custom segment.`,
      [IntegrationType.MailchimpWebhook]: `All exported users will be sent as a Mailchimp custom segment.`,
      [IntegrationType.TaplyticsWebhook]: `All exported users will be sent as a Taplytics custom segment.`,
      [IntegrationType.VwoWebhook]: `All exported users will be sent as a VWO custom segment.`,
      [IntegrationType.WebengageWebhook]: `All exported users will be sent as a WebEngage custom segment.`,
      [IntegrationType.XtremepushWebhook]: `All exported users will be sent as an Xtremepush custom segment.`,
    },
    title: `One-time export`,
    type: `static`,
  },
  {
    text: {
      [IntegrationType.Braze]: `Your Braze cohort will be updated every 2 hours to match users in Mixpanel cohort.`,
      [IntegrationType.Marketo]: `The custom field will be updated every 2 hours to match users in the cohort.`,
      [IntegrationType.UrbanAirship]: `Tag will be updated every 2 hours to match users in the cohort.`,
      [IntegrationType.FacebookAds]: `Your Facebook Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.GoogleAds]: `Your Google Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.Iterable]: `Your Iterable List will be updated every 15 minutes to match users in this cohort.`,
      [IntegrationType.TestPartnerWebhookIntegration]: `All users in the cohort will exported every 2 hours to the Webhook destination of choice.`,
      [IntegrationType.MoengageWebhook]: `Your MoEngage custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.LeanplumWebhook]: `Your Leanplum Audience will be updated every day with users in this cohort who successfully match into Leanplum.`,
      [IntegrationType.ChameleonWebhook]: `Your Chameleon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.InsiderWebhook]: `Your Insider custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.AppcuesWebhook]: `Your Appcues custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.OnesignalWebhook]: `Your OneSignal custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.KameleoonWebhook]: `Your Kameleoon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.MailchimpWebhook]: `Your Mailchimp custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.TaplyticsWebhook]: `Your Taplytics custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.VwoWebhook]: `Your VWO custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.WebengageWebhook]: `Your Webengage custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.XtremepushWebhook]: `Your Xtremepush custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
    },
    title: `Dynamic sync`,
    type: `dynamic`,
  },
];
